<template>
  <div class="the-navbar__user-meta flex items-center">
    <div
      v-if="userData && userData.firstName"
      class="text-right leading-tight hidden sm:block items-center mr-3"
    >
      <!-- <h6>{{ userData.firstName }} {{ userData.lastName }}</h6> -->
    </div>

    <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
      <div class="con-img">
        <vs-avatar
          v-if="userData && userData.profilePictureUrl"
          class="m-0"
          size="32px"
          :src="constants.s3Bucket + '/editor/profile-pictures/' + userData.profilePictureUrl"
        />
        <vs-avatar
          v-else
          class="m-0"
          size="32px"
          :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(
            userData.firstName,
            userData.lastName
          )}`"
        />
      </div>

      <vs-dropdown-menu class="vx-navbar-dropdown p-0">
        <ul style="min-width: 17rem;">
          
          <!-- HEADER WITH ICON & ORG COUNT -->
          <li class="px-4 py-3 flex items-center justify-between text-sm font-medium border-b border-gray-100 cursor-default" @click.stop>
            <div class="flex items-center text-gray-700">
              <vs-icon icon="business_center" size="18px" class="mr-2 text-gray-500" />
              <span>{{ $t('orgDashboard.main.myOrganizations') || 'My Organizations' }}</span>
            </div>
            <!-- Count Badge -->
            <span v-if="totalOrganizations > 0" class="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center justify-center">
              {{ totalOrganizations }}
            </span>
          </li>

          <!-- SCROLLABLE ORGANIZATION LIST -->
          <div class="overflow-y-auto" style="max-height: 220px;">
            <template v-if="totalOrganizations > 0">
              
              <!-- SUPER ORGS -->
              <li v-for="(myOrgs, index) in myOrganizations.superOrgs"
                  :key="'superOrg-' + index"
                  class="py-2 px-4 flex items-center cursor-pointer text-gray-700 hover:bg-primary hover:text-white transition-colors"
                  @click="manageOrganization(myOrgs.slug || myOrgs._id)">
                <vs-avatar
                  v-if="myOrgs.logo"
                  class="mr-3 m-0"
                  size="24px"
                  :src="constants.s3Bucket + '/organization/logo/' + myOrgs.logo"
                />
                <vs-avatar
                  v-else
                  class="mr-3 m-0"
                  size="24px"
                  :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
                />
                <span class="text-sm font-medium truncate">{{ myOrgs.name }}</span>
              </li>
              
              <!-- SUB ORGS -->
              <li v-for="(myOrgs, index) in myOrganizations.subOrgs"
                  :key="'subOrg-' + index"
                  class="py-2 px-4 flex items-center cursor-pointer text-gray-700 hover:bg-primary hover:text-white transition-colors"
                  @click="(myOrgs.status === 'active' || myOrgs.status === 'ACTIVE') ? manageOrganization(myOrgs.organization[0].slug || myOrgs.organization[0]._id) : acceptAdminOrgInvite(myOrgs._id)">
                <vs-avatar
                  v-if="myOrgs.organization[0] && myOrgs.organization[0].logo"
                  class="mr-3 m-0"
                  size="24px"
                  :src="constants.s3Bucket + '/organization/logo/' + myOrgs.organization[0].logo"
                />
                <vs-avatar
                  v-else
                  class="mr-3 m-0"
                  size="24px"
                  :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
                />
                <span class="text-sm font-medium truncate">{{ myOrgs.organization[0].name }}</span>
                <span v-if="myOrgs.status !== 'active' && myOrgs.status !== 'ACTIVE'" class="ml-auto text-warning text-xs bg-white px-1 rounded">{{ $t('orgDashboard.main.pendingInvitation') }}</span>
              </li>
              
            </template>

            <li v-else class="py-4 px-4 text-center text-sm text-gray-400 cursor-default">
              {{ $t('orgDashboard.main.noOrganizationFound') }}
            </li>
          </div>

          <vs-divider class="m-0" />

          <!-- CREATE & VIEW ALL ORGS -->
          <li
            class="flex py-2 px-4 cursor-pointer text-gray-700 hover:bg-primary hover:text-white transition-colors mt-1"
            @click="$router.push('/organization/create').catch(() => {})"
          >
            <vs-icon icon="add_circle_outline" size="18px" />
            <span class="ml-2 text-sm">{{ $t('orgDashboard.userDashboard.profileDropDown.createOrganization') || 'Create Organization' }}</span>
          </li>

          <vs-divider class="m-1" />

          <!-- LANGUAGE SWITCHER -->
          <li class="flex items-center py-2 px-4 cursor-default mb-1 text-gray-700">
            <feather-icon icon="GlobeIcon" svgClasses="w-4 h-4 text-gray-500" />
            <div class="flex items-center ml-3 space-x-2">
              <div
                @click="changeLanguage('en')"
                class="flex items-center px-2 py-1 rounded cursor-pointer text-xs font-semibold transition-colors"
                :class="$i18n.locale === 'en' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100'"
              >
                <img src="https://flagcdn.com/w20/us.png" alt="EN" class="w-4 h-3 mr-1" />
                EN
              </div>
              <div
                @click="changeLanguage('pt')"
                class="flex items-center px-2 py-1 rounded cursor-pointer text-xs font-semibold transition-colors"
                :class="$i18n.locale === 'pt' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100'"
              >
                <img src="https://flagcdn.com/w20/pt.png" alt="PT" class="w-4 h-3 mr-1" />
                PT
              </div>
            </div>
          </li>

          <vs-divider class="m-1" />
          
          <!-- LOGOUT (NO SETTINGS) -->
          <li
            class="flex py-2 px-4 cursor-pointer text-gray-700 hover:bg-primary hover:text-white transition-colors mb-1"
            @click="logout"
          >
            <feather-icon icon="LogOutIcon" svgClasses="w-4 h-4" />
            <span class="ml-2 text-sm">{{ $t('orgDashboard.userDashboard.profileDropDown.logout') }}</span>
          </li>
        </ul>
      </vs-dropdown-menu>

    <!-- MISSING CLOSING TAG ADDED HERE -->
    </vs-dropdown> 

  </div>
</template>

<script>
import constants from "@/constants";

export default {
  data() {
    return {
      constants,
    };
  },
  computed: {
    userData() {
      return this.$store.state.users.data;
    },
    avatarNameGenerator() {
      return this.$store.state.users.avatarNameGenerator;
    },
    
    // FETCH AND FILTER ORGANIZATIONS SAFELY
    myOrganizations() {
      const orgs = this.$store.state.organization.myOrganizations;
      if (!orgs) return { superOrgs: [], subOrgs: [] };

      // Exact same filter from your original working code
      const superOrgs = (orgs.superOrgs || []).filter(org => {
        return org && org.status !== 'DEACTIVATED';
      });

      // Exact same filter from your original working code
      const subOrgs = (orgs.subOrgs || []).filter(org => {
        return org && 
               org.status !== 'DEACTIVATED' && 
               org.organization && 
               org.organization.length > 0;
      });

      return { superOrgs, subOrgs };
    },
    
    // YELLOW BADGE COUNT
    totalOrganizations() {
      return this.myOrganizations.superOrgs.length + this.myOrganizations.subOrgs.length;
    }
  },
  created() {
    try {
      if (this.$store.state.auth && this.$store.state.auth.user) {
        const payload = {
          email: this.$store.state.auth.user.email,
          userId: this.$store.state.auth.user._id,
        };
        this.$store
          .dispatch("organization/fetchAllMyOrganizations", payload)
          .catch((err) => {
            console.error("Error fetching organizations:", err);
          });
      }
    } catch (error) {
      console.error("Error in ProfileDropDown created hook:", error);
    }
  },
  methods: {
    acceptAdminOrgInvite(adminId) {
      this.$store
        .dispatch("organization/acceptAdminOrgInvite", { adminId })
        .then(() => {
          window.location.href = "/organization/my-organizations";
        })
        .catch((err) => {
          throw err;
        });
    },
    manageOrganization(organizationIdORSlug) {
      window.location.href = `/organization/${organizationIdORSlug}/home`;
    },
    changeLanguage(locale) {
      this.$i18n.locale = locale;
      localStorage.setItem('userLanguage', locale);
    },
    logout() {
      this.$store.dispatch("alex/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("boards/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("editor/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("auth/logout").then(() => {
        window.location.href = "/";
      });
    },
  },
};
</script>

<style scoped>
/* Optional: Make the scrollbar slightly thinner for a cleaner look */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 20px;
}
</style>